import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const SITE_URL = "https://lalitasoujanya.com";
export const BRAND = "Lalita Soujanya";
const DEFAULT_IMAGE = `${SITE_URL}/assets/editorial_banner.jpg`;

const setMeta = (selector, attributes) => {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    document.head.appendChild(node);
  }
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
};

const absoluteUrl = (value) => {
  if (!value) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(value)) return value;
  return `${SITE_URL}${value.startsWith("/") ? "" : "/"}${value}`;
};

export default function Seo({ title, description, path, image, type = "website", robots = "index, follow, max-image-preview:large", schema, keywords, productPrice }) {
  const location = useLocation();

  useEffect(() => {
    const canonicalPath = path || location.pathname || "/";
    const canonical = `${SITE_URL}${canonicalPath === "/" ? "/" : canonicalPath.replace(/\/$/, "")}`;
    const fullTitle = title.includes(BRAND) ? title : `${title} | ${BRAND}`;
    const socialImage = absoluteUrl(image);

    document.title = fullTitle;
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[name="robots"]', { name: "robots", content: robots });
    if (keywords) setMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
    else document.head.querySelector('meta[name="keywords"]')?.remove();
    setMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:type"]', { property: "og:type", content: type });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    setMeta('meta[property="og:image"]', { property: "og:image", content: socialImage });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: BRAND });
    setMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_IN" });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: socialImage });
    if (type === "product" && productPrice != null) {
      setMeta('meta[property="product:price:amount"]', { property: "product:price:amount", content: String(productPrice) });
      setMeta('meta[property="product:price:currency"]', { property: "product:price:currency", content: "INR" });
    } else {
      document.head.querySelector('meta[property="product:price:amount"]')?.remove();
      document.head.querySelector('meta[property="product:price:currency"]')?.remove();
    }

    let canonicalNode = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalNode) {
      canonicalNode = document.createElement("link");
      canonicalNode.rel = "canonical";
      document.head.appendChild(canonicalNode);
    }
    canonicalNode.href = canonical;

    document.getElementById("page-structured-data")?.remove();
    if (schema) {
      const node = document.createElement("script");
      node.id = "page-structured-data";
      node.type = "application/ld+json";
      node.textContent = JSON.stringify(schema);
      document.head.appendChild(node);
    }
  }, [description, image, keywords, location.pathname, path, productPrice, robots, schema, title, type]);

  return null;
}

const ROUTE_SEO = {
  "/": {
    title: "Designer Occasion Wear in Hyderabad | Lalita Soujanya",
    description: "Shop contemporary Indian designer wear in Hyderabad by Lalita Soujanya. Discover women's, men's and kids' occasion wear across Telangana and Andhra Pradesh.",
  },
  "/shop": {
    title: "Designer Wear Online in Hyderabad",
    description: "Explore Lalita Soujanya designer collections for women, men and kids, with styling and customisation support from our Hyderabad studio.",
  },
  "/about": {
    title: "About Our Hyderabad Fashion House",
    description: "Meet Lalita Soujanya, a contemporary Indian fashion house in Hyderabad creating expressive occasion wear rooted in craftsmanship.",
  },
  "/customization": {
    title: "Custom Designer Wear in Hyderabad",
    description: "Enquire about personalised designer wear, custom sizing and occasion styling in Hyderabad for Telangana and Andhra Pradesh clients.",
  },
  "/contact": {
    title: "Contact Lalita Soujanya Hyderabad",
    description: "Contact our Hyderabad studio on WhatsApp or Instagram for designer wear, sizing, customisation, availability and order assistance.",
  },
  "/policies": {
    title: "Shopping Policies",
    description: "Read Lalita Soujanya privacy, terms, returns and shipping policies for online designer wear orders and studio enquiries.",
  },
};

export function RouteSeo() {
  const location = useLocation();
  const privateRoute = /^(\/admin|\/account|\/cart|\/checkout|\/thank-you|\/login|\/register|\/wishlist)/.test(location.pathname);
  if (privateRoute) return <Seo title="Private page" description="Lalita Soujanya customer and administration page." robots="noindex, nofollow" />;
  if (location.pathname.startsWith("/product/") || location.pathname.startsWith("/shop/")) return null;
  return <Seo {...(ROUTE_SEO[location.pathname] || ROUTE_SEO["/"])} />;
}
